import { faPlus, faTrash, faTrashAlt } from "@fortawesome/free-solid-svg-icons";
import { useFormikContext } from "formik";
import { cloneDeep } from "lodash";
import React, { useState } from "react";
import { Button } from "react-bootstrap";

import { TDSImmovablePropertyDetailsForm } from ".";
import { IFormITR1, IImmovableProperties } from "../../../config/interfaces";
import FASvgIcon from "../../FASvgIcon";
import PreventEditComponent from "../../PreventEditComponent";
import PriceDisplay from "../../PriceDisplay";
import { ConfirmationModal } from "../../modals";

const TDSImmovablePropertyDetails: React.FC = () => {
  const { values, setFieldValue } = useFormikContext<IFormITR1>();

  const [showAddFormCanvas, setShowAddFormCanvas] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<IImmovableProperties | null>(null);
  const [activeItem, setActiveItem] = useState<IImmovableProperties | null>(null);
  // used in both edit and delete cases
  const [activeItemIdx, setActiveItemIdx] = useState<string>("");

  const ImmovableProperties = cloneDeep(values.TaxesPaidTDSandTCS?.ImmovableProperties || []);

  const handleDeleteItem = async () => {
    ImmovableProperties.splice(Number(activeItemIdx), 1);
    setFieldValue("TaxesPaidTDSandTCS.ImmovableProperties", ImmovableProperties);
    setShowDeleteModal(() => null);
    setActiveItem(() => null);
    setActiveItemIdx(() => "");
    handleTotalCalculation();
  };

  const handleTotalCalculation = () => {
    const _TotalTDS3Details =
      ImmovableProperties?.reduce((acc, curr) => {
        const value = Number(curr.TaxDeductedClaimedForThisYear);
        return acc + (isNaN(value) ? 0 : value);
      }, 0) || 0;

    setFieldValue("TotalTDS3Details", _TotalTDS3Details);
    setFieldValue("TaxesPaidTDSandTCS.ImmovableProperties", ImmovableProperties);
  };

  return (
    <>
      <section className="mb-5">
        <div className="section-title-wrap">
          <h5>
            TDS on Sale or Rent of Immovable Property
            <small>
              If you sell/rent land, house, property or building etc (immovable property) etc, the
              buyer/tenant may have deducted TDS at the rate of 1% on the sale price of the
              property/rental and issued you a TDS certificate. You need to specify those TDS
              deduction details here.
            </small>
          </h5>
          <PreventEditComponent>
            <Button variant="secondary" size="sm" onClick={() => setShowAddFormCanvas(() => true)}>
              <FASvgIcon icon={faPlus} />
              Add
            </Button>
          </PreventEditComponent>
        </div>

        <dl className="eds-list eds-dl eds-inline-between">
          {ImmovableProperties?.map((tds, index) => (
            <div key={`sale-rent-deduction-${index}`} className="eds-list--item">
              <dt>{tds.BuyerName}</dt>
              <dd>
                <span>
                  <PriceDisplay price={parseInt(tds?.TaxDeductedClaimedForThisYear || "0")} />
                </span>
                <Button
                  variant="link"
                  onClick={() => {
                    setShowAddFormCanvas(() => true);
                    setActiveItem(() => tds);
                    setActiveItemIdx(() => String(index));
                  }}
                >
                  View
                  <PreventEditComponent>
                    <>/Edit</>
                  </PreventEditComponent>
                </Button>
                <Button
                  variant="transparent"
                  className="icon-only"
                  onClick={() => {
                    setShowDeleteModal(() => tds);
                    setActiveItemIdx(() => String(index));
                  }}
                >
                  <FASvgIcon icon={faTrash} />
                </Button>
              </dd>
            </div>
          ))}
        </dl>
      </section>

      <TDSImmovablePropertyDetailsForm
        showCanvas={showAddFormCanvas}
        handleCloseBtnPress={(tdsData) => {
          if (tdsData) {
            if (activeItem) {
              ImmovableProperties?.splice(Number(activeItemIdx), 1, tdsData);
            } else {
              ImmovableProperties?.push(tdsData);
            }
            handleTotalCalculation();
          }
          setShowAddFormCanvas(() => false);
          setActiveItem(() => null);
          setActiveItemIdx(() => "");
        }}
        formValues={activeItem}
      />

      <ConfirmationModal
        showModal={!!showDeleteModal}
        iconColorClass="text-danger"
        title="Are you sure?"
        message={<p>You want to delete {showDeleteModal?.BuyerName}</p>}
        onYesBtnClick={() => handleDeleteItem()}
        onNoBtnClick={() => setShowDeleteModal(() => null)}
        icon={faTrashAlt}
      />
    </>
  );
};

export default TDSImmovablePropertyDetails;
